// File: properties/CustomerProfileProperties.jsx
import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Select,
  Button,
  Divider,
  Alert,
  AlertIcon,
  AlertDescription,
  IconButton,
  Tag,
  Flex,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiDatabase } from 'react-icons/fi';

export default function CustomerProfileProperties({
  nodeConfig,
  setNodeConfig,
  updateNodeData,
  setSelectedNode,
  selectedNode,
  edges,
  nodes,
}) {
  // ── Helpers ────────────────────────────────────────────────

  const conditions = nodeConfig.conditions || [];

  const addCondition = () => {
    setNodeConfig({
      ...nodeConfig,
      conditions: [
        ...conditions,
        { profile_key: '', operator: 'equals', match_value: '' },
      ],
    });
  };

  const removeCondition = (index) => {
    const updated = [...conditions];
    updated.splice(index, 1);
    setNodeConfig({ ...nodeConfig, conditions: updated });
  };

  const updateCondition = (index, field, value) => {
    const updated = [...conditions];
    // When operator changes to is_empty / is_not_empty, clear match_value
    if (field === 'operator') {
      updated[index] = { ...updated[index], operator: value, match_value: '' };
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    setNodeConfig({ ...nodeConfig, conditions: updated });
  };

  const noMatchValueNeeded = (operator) =>
    operator === 'is_empty' || operator === 'is_not_empty';

  // ── Render ─────────────────────────────────────────────────

  return (
    <VStack spacing={4} align="stretch">
      {/* Step Name */}
      <Box>
        <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1.5}>
          Step Name
        </Text>
        <Input
          size="sm"
          value={nodeConfig.label || ''}
          onChange={(e) => setNodeConfig({ ...nodeConfig, label: e.target.value })}
          placeholder="e.g., Check City & Tier"
        />
      </Box>

      <Divider />

      {/* ── Conditions ─────────────────────────────────────── */}
      <Box>
        <HStack justify="space-between" mb={3}>
          <Text fontSize="xs" fontWeight="semibold" color="gray.600">
            Profile Conditions
          </Text>
          <Button
            size="xs"
            variant="outline"
            leftIcon={<FiPlus size={10} />}
            onClick={addCondition}
          >
            Add Condition
          </Button>
        </HStack>

        {/* Empty state */}
        {conditions.length === 0 && (
          <Flex
            direction="column"
            align="center"
            justify="center"
            py={6}
            border="2px dashed"
            borderColor="gray.200"
            borderRadius="lg"
            bg="gray.50"
          >
            <Box color="gray.400" mb={2}>
              <FiDatabase size={22} />
            </Box>
            <Text fontSize="xs" color="gray.500">
              No conditions added yet
            </Text>
            <Text fontSize="10px" color="gray.400" mt={0.5}>
              All conditions are combined with AND logic
            </Text>
          </Flex>
        )}

        {/* Condition rows */}
        <VStack spacing={3} align="stretch">
          {conditions.map((condition, index) => (
            <Box
              key={index}
              p={3}
              border="1px solid"
              borderColor="orange.200"
              borderRadius="lg"
              bg="orange.50"
            >
              {/* Row header */}
              <HStack justify="space-between" mb={2}>
                <Tag
                  colorScheme="orange"
                  fontSize="10px"
                  variant="subtle"
                  textTransform="none"
                >
                  Condition {index + 1}
                </Tag>
                <IconButton
                  icon={<FiTrash2 size={11} />}
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => removeCondition(index)}
                  aria-label="Remove condition"
                />
              </HStack>

              {/* Profile Attribute */}
              <Box mb={2}>
                <Text fontSize="10px" fontWeight="semibold" color="gray.600" mb={1}>
                  Profile Attribute
                </Text>
                <Select
                  size="sm"
                  value={condition.profile_key || ''}
                  onChange={(e) => updateCondition(index, 'profile_key', e.target.value)}
                  placeholder="Select attribute"
                >
                  <optgroup label="Basic Info">
                    <option value="first_name">First Name</option>
                    <option value="last_name">Last Name</option>
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="gender">Gender</option>
                    <option value="age">Age</option>
                  </optgroup>
                  <optgroup label="Location">
                    <option value="city">City</option>
                    <option value="country">Country</option>
                    <option value="language">Language</option>
                  </optgroup>
                  <optgroup label="Professional">
                    <option value="company">Company</option>
                    <option value="job_title">Job Title</option>
                  </optgroup>
                  <optgroup label="Engagement">
                    <option value="subscription_status">Subscription Status</option>
                    <option value="customer_tier">Customer Tier</option>
                    <option value="tags">Tags</option>
                  </optgroup>
                </Select>
              </Box>

              {/* Operator */}
              <Box mb={noMatchValueNeeded(condition.operator) ? 0 : 2}>
                <Text fontSize="10px" fontWeight="semibold" color="gray.600" mb={1}>
                  Operator
                </Text>
                <Select
                  size="sm"
                  value={condition.operator || 'equals'}
                  onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                >
                  <option value="equals">Equals (=)</option>
                  <option value="not_equals">Not Equals (≠)</option>
                  <option value="contains">Contains</option>
                  <option value="starts_with">Starts With</option>
                  <option value="ends_with">Ends With</option>
                  <option value="greater_than">Greater Than</option>
                  <option value="less_than">Less Than</option>
                  <option value="is_empty">Is Empty</option>
                  <option value="is_not_empty">Is Not Empty</option>
                </Select>
              </Box>

              {/* Match Value — hidden for is_empty / is_not_empty */}
              {!noMatchValueNeeded(condition.operator) && (
                <Box>
                  <Text fontSize="10px" fontWeight="semibold" color="gray.600" mb={1}>
                    Match Value
                  </Text>
                  <Input
                    size="sm"
                    value={condition.match_value || ''}
                    onChange={(e) =>
                      updateCondition(index, 'match_value', e.target.value)
                    }
                    placeholder="e.g., Mumbai, Male, Premium"
                  />
                </Box>
              )}
            </Box>
          ))}
        </VStack>

        {/* AND logic notice — only show when more than one condition */}
        {conditions.length > 1 && (
          <Alert status="warning" borderRadius="md" mt={3} py={2} px={3}>
            <AlertIcon boxSize="14px" />
            <AlertDescription fontSize="xs">
              <strong>AND logic:</strong> All {conditions.length} conditions must match
              for the True path.
            </AlertDescription>
          </Alert>
        )}
      </Box>

      <Divider />

      {/* ── Branch Labels ───────────────────────────────────── */}
      <Box>
        <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={3}>
          Branch Labels
        </Text>
        <VStack spacing={3} align="stretch">
          <Box>
            <Text fontSize="10px" fontWeight="semibold" color="gray.600" mb={1}>
              True Path Label
            </Text>
            <Input
              size="sm"
              value={nodeConfig.trueLabel || ''}
              onChange={(e) => setNodeConfig({ ...nodeConfig, trueLabel: e.target.value })}
              placeholder="Match Found (True)"
            />
          </Box>
          <Box>
            <Text fontSize="10px" fontWeight="semibold" color="gray.600" mb={1}>
              False Path Label
            </Text>
            <Input
              size="sm"
              value={nodeConfig.falseLabel || ''}
              onChange={(e) =>
                setNodeConfig({ ...nodeConfig, falseLabel: e.target.value })
              }
              placeholder="No Match (False)"
            />
          </Box>
        </VStack>
      </Box>

      {/* ── Info ────────────────────────────────────────────── */}
      <Alert status="info" borderRadius="md" py={2} px={3}>
        <AlertIcon boxSize="14px" />
        <AlertDescription fontSize="xs">
          Checks customer profile attributes. All conditions must match to follow the{' '}
          <strong>True</strong> path; otherwise the <strong>False</strong> path is taken.
        </AlertDescription>
      </Alert>

      {/* ── Save ────────────────────────────────────────────── */}
      <Button
        size="sm"
        w="full"
        bg="#FBAF3F"
        color="white"
        _hover={{ bg: '#F9A825' }}
        onClick={updateNodeData}
      >
        Update Node
      </Button>
    </VStack>
  );
}